import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton } from "@mui/material"
import { CloseOutlined } from "@mui-symbols-material/w300"
import type { FunctionComponent } from "react"
import { useCallback, useState } from "react"
import { type FieldPath, useFormContext } from "react-hook-form"

import type { AddPricingValues } from "./AddPricingSchema"
import { AddProductPriceBreakdown } from "./AddProductInformation"
import { AddShippingTerms } from "./AddShippingTerms"

import { Button } from "@/components/common/Button"
import type { Product } from "@/graphql/codegen/graphql"
import type { DialogCloseAction } from "@/types/common"

interface AddPricingTiersModalProps {
  open: boolean
  product: Product
  onClose: (action: DialogCloseAction) => void
  onSubmit: (data: AddPricingValues) => void
}

const AddPricingTiersModal: FunctionComponent<AddPricingTiersModalProps> = ({ open, product, onClose, onSubmit }) => {
  const [step, setStep] = useState(0)
  const methods = useFormContext<AddPricingValues>()
  const { watch, getValues, trigger, reset } = methods

  const pricingPackageId = watch("pricingPackageId")
  const effectiveDate = watch("effectiveDate")
  const expirationDate = watch("expirationDate")

  const onNextStep = useCallback(async () => {
    const fields = getValues()

    const fieldsToValidate = (Object.keys(fields) as FieldPath<AddPricingValues>[]).filter(
      (field) =>
        !(
          step === 0 &&
          (field === "freightTerms" ||
            field === "incoterms" ||
            field === "incotermsLocationId" ||
            field === "pricingTiersToCreate")
        )
    )
    const isValid = await trigger(fieldsToValidate)

    if (isValid) {
      setStep((prevState) => prevState + 1)
    }
  }, [getValues, step, trigger])

  const onBackClick = useCallback(() => {
    setStep((prevState) => prevState - 1)
  }, [])

  const handleResetClick = useCallback(() => {
    reset()
    setStep(0)
    onClose("cancel")
  }, [reset, onClose])

  const handleOnSubmit = useCallback(
    (value: AddPricingValues) => {
      onSubmit(value)
      setStep(0)
      onClose("success")
    },
    [onClose, onSubmit]
  )

  const handleClose = useCallback(
    (reason: DialogCloseAction) => {
      reset()
      setStep(0)
      onClose(reason)
    },
    [reset, onClose]
  )

  const priceTierFields = watch("pricingTiersToCreate")

  const allFieldsValid = priceTierFields.every((_, index: number) => {
    const { unitOfMeasurement, numberOfUnits, pricePerUnit } = methods.getValues(`pricingTiersToCreate.${index}`)

    const [uomError, unitsError, priceError] = [
      methods.getFieldState(`pricingTiersToCreate.${index}.unitOfMeasurement.id`, methods.formState).error,
      methods.getFieldState(`pricingTiersToCreate.${index}.numberOfUnits`, methods.formState).error,
      methods.getFieldState(`pricingTiersToCreate.${index}.pricePerUnit`, methods.formState).error,
    ]
    const noErrors = !uomError && !unitsError && !priceError

    if (index === 0) {
      // First tier is required: UoM, numberOfUnits, and pricePerUnit must all be present
      return unitOfMeasurement?.id && numberOfUnits?.trim() && pricePerUnit?.trim() && noErrors
    }

    // For tiers 2+: UoM is required, but numberOfUnits and pricePerUnit are optional
    // If present, both numberOfUnits and pricePerUnit must be filled together
    const hasNumberOfUnits = numberOfUnits?.trim()
    const hasPricePerUnit = pricePerUnit?.trim()
    return (
      unitOfMeasurement?.id &&
      noErrors &&
      ((hasNumberOfUnits && hasPricePerUnit) || (!hasNumberOfUnits && !hasPricePerUnit))
    )
  })

  const effectiveDateState = methods.getFieldState("effectiveDate", methods.formState)
  const expirationDateState = methods.getFieldState("expirationDate", methods.formState)

  const isPriceBreakdownDisabled =
    allFieldsValid && !effectiveDateState.invalid && effectiveDate && (!expirationDateState.invalid || !expirationDate)

  return (
    <Dialog open={open} onClose={(_, reason) => handleClose(reason)} fullWidth>
      <DialogTitle className='px-[38px] font-normal text-primary'>
        {pricingPackageId ? "Edit" : "Add"} Pricing
      </DialogTitle>
      <IconButton
        aria-label='close'
        onClick={() => handleClose("cancel")}
        className='absolute right-4 top-4 text-primary'
      >
        <CloseOutlined />
      </IconButton>
      <Divider />
      <DialogContent className='px-[38px]'>
        {step === 0 && <AddProductPriceBreakdown product={product} />}
        {step === 1 && <AddShippingTerms product={product} />}
      </DialogContent>
      <DialogActions className='justify-between px-10 pb-10'>
        <Button appearance='outlined' onClick={handleResetClick}>
          Cancel
        </Button>
        {step === 0 && (
          <Button onClick={onNextStep} disabled={!isPriceBreakdownDisabled}>
            Continue
          </Button>
        )}
        {step === 1 && (
          <Box>
            <Button variant='primary' appearance='text' className='mr-6' onClick={onBackClick}>
              Go Back
            </Button>
            <Button disabled={!methods.formState.isValid} onClick={() => handleOnSubmit(getValues())}>
              {pricingPackageId ? "Update" : "Save"}
            </Button>
          </Box>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default AddPricingTiersModal
